import React, { useState, useMemo, useEffect } from "react";
import { motion } from "framer-motion";
import { RotateCcw, Smartphone, Wifi, CheckCircle, AlertCircle, Lightbulb, RefreshCw, Activity, Zap } from "lucide-react";
import { useTranslation } from "react-i18next";

interface RemoteRebootWidgetProps {
    color?: string;
}

const RemoteRebootWidget: React.FC<RemoteRebootWidgetProps> = ({ color = "from-cyan-400 to-teal-500" }) => {
    const { t, i18n } = useTranslation("nonHealth");
    const [rebooting, setRebooting] = useState(false);
    const [rebooted, setRebooted] = useState(false);
    
    // Dynamic data simulation
    const [lastRebootTime, setLastRebootTime] = useState<string>("");
    const [todayRebootCount, setTodayRebootCount] = useState(0);

    useEffect(() => {
        // Simulate dynamic data initialization
        const date = new Date();
        date.setDate(date.getDate() - 1);
        date.setHours(21, 0);
        setLastRebootTime(new Intl.DateTimeFormat(i18n.language, {
            weekday: 'long', hour: '2-digit', minute: '2-digit', hour12: false
        }).format(date));
        
        setTodayRebootCount(Math.floor(Math.random() * 3));
    }, [i18n.language]);

    const handleReboot = () => {
        if (rebooting || rebooted) return;
        setRebooting(true);
        setTimeout(() => {
            setRebooting(false);
            setRebooted(true);
            setTodayRebootCount(prev => prev + 1);
            setTimeout(() => setRebooted(false), 3000);
        }, 3000);
    };

    const deviceInfo = useMemo(() => [
        { label: t("widgets.remoteReboot.deviceInfo.device"), value: t("widgets.remoteReboot.deviceInfo.watchName"), icon: <Smartphone className="w-4 h-4" />, color: "text-cyan-600" },
        { label: t("widgets.remoteReboot.deviceInfo.connection"), value: t("widgets.remoteReboot.deviceInfo.wifiConnected"), icon: <Wifi className="w-4 h-4" />, color: "text-teal-600" },
        {
            label: t("widgets.remoteReboot.deviceInfo.status"),
            value: rebooted ? t("widgets.remoteReboot.status.backOnline") : rebooting ? t("widgets.remoteReboot.status.rebooting") : t("widgets.remoteReboot.status.active"),
            icon: <Activity className="w-4 h-4" />,
            color: rebooted ? "text-green-600" : rebooting ? "text-amber-600" : "text-teal-600",
        },
    ], [rebooted, rebooting, t]);

    return (
        <motion.div
            key="remoteReboot-widget"
            className="relative w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.5, type: "spring", stiffness: 100 }}
        >
            <div className="bg-white/80 backdrop-blur-xl rounded-2xl p-5 shadow-xl border border-cyan-200 relative overflow-hidden">
                {/* افکت‌های تزئینی پس‌زمینه */}
                <motion.div
                    className="absolute -left-10 top-10 w-40 h-40 bg-gradient-to-br from-cyan-400/15 to-teal-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4], rotate: [0, 15, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
                />
                <motion.div
                    className="absolute -right-10 -bottom-10 w-40 h-40 bg-gradient-to-tr from-teal-400/15 to-cyan-500/10 rounded-full blur-2xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.4, 0.7, 0.4], rotate: [0, -15, 0] }}
                    transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                />

                {/* هدر */}
                <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center gap-2">
                        <motion.div
                            className={`w-9 h-9 rounded-full bg-gradient-to-r ${color} flex items-center justify-center text-white shadow-md`}
                            animate={rebooting ? { rotate: [0, 360] } : { scale: [1, 1.05, 1] }}
                            transition={rebooting ? { duration: 1, repeat: Infinity, ease: "linear" } : { duration: 2, repeat: Infinity }}
                        >
                            <RotateCcw className="w-5 h-5" />
                        </motion.div>
                        <h5 className="text-teal-800 font-semibold text-lg">{t("widgets.remoteReboot.title")}</h5>
                    </div>
                    <motion.div
                        className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium text-white shadow-sm bg-gradient-to-r ${
                            rebooted ? "from-green-500 to-emerald-600" : rebooting ? "from-amber-500 to-orange-500" : "from-cyan-500 to-teal-600"
                        }`}
                        animate={{ scale: [1, 1.05, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                    >
                        <motion.span className="inline-block w-2 h-2 rounded-full bg-white ml-1" animate={{ opacity: rebooting ? [1, 0.3, 1] : 1 }} transition={{ duration: 0.8, repeat: Infinity }} />
                        {rebooted ? t("widgets.remoteReboot.status.success") : rebooting ? t("widgets.remoteReboot.status.restarting") : t("widgets.remoteReboot.status.ready")}
                    </motion.div>
                </div>

                {/* بخش اصلی - وضعیت دستگاه */}
                <div className={`bg-gradient-to-br from-cyan-50 to-teal-100 p-4 rounded-xl mb-4 shadow-inner relative overflow-hidden border border-cyan-200/50`}>
                    {/* نوار وضعیت بالا */}
                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-cyan-300/50 via-teal-500/80 to-cyan-300/50 overflow-hidden">
                        <motion.div
                            className="absolute inset-0 bg-gradient-to-r from-transparent via-white/60 to-transparent"
                            animate={rebooting ? { x: ["-100%", "100%"] } : { x: "-100%" }}
                            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                    </div>

                    {/* اطلاعات دستگاه */}
                    <div className="space-y-3 mt-2">
                        {deviceInfo.map((item, idx) => (
                            <motion.div
                                key={idx}
                                className="flex items-center justify-between bg-white/60 backdrop-blur-sm px-3 py-2 rounded-lg border border-cyan-100 shadow-sm"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * idx }}
                                whileHover={{ y: -1, boxShadow: "0 4px 8px rgba(6, 182, 212, 0.15)" }}
                            >
                                <span className="text-gray-600 text-xs flex items-center gap-1">
                                    <span className={item.color}>{item.icon}</span>
                                    {item.label}
                                </span>
                                <motion.span
                                    className={`text-xs font-semibold ${item.color}`}
                                    animate={idx === 2 && rebooting ? { opacity: [1, 0.5, 1] } : {}}
                                    transition={{ duration: 0.8, repeat: Infinity }}
                                >
                                    {item.value}
                                </motion.span>
                            </motion.div>
                        ))}
                    </div>

                    {/* انیمیشن ری‌استارت */}
                    {rebooting && (
                        <motion.div className="mt-3 flex items-center justify-center gap-2" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                            {[0, 1, 2, 3, 4].map((i) => (
                                <motion.div
                                    key={i}
                                    className="w-2 h-2 rounded-full bg-teal-500"
                                    animate={{ scale: [1, 1.5, 1], opacity: [0.5, 1, 0.5] }}
                                    transition={{ duration: 0.8, repeat: Infinity, delay: i * 0.15 }}
                                />
                            ))}
                        </motion.div>
                    )}
                </div>

                {/* کارت‌های اطلاعاتی */}
                <div className="grid grid-cols-2 gap-3 mb-4 text-xs">
                    <motion.div
                        className="bg-gradient-to-br from-cyan-50 to-cyan-100/70 p-3 rounded-xl shadow-sm border border-cyan-200 relative overflow-hidden"
                        whileHover={{ scale: 1.03, y: -3, boxShadow: "0 8px 20px rgba(6, 182, 212, 0.15)" }}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.3 }}
                    >
                        <div className="text-cyan-700 font-bold mb-1 flex items-center gap-1">
                            <Zap className="w-3.5 h-3.5" />
                            {t("widgets.remoteReboot.stats.lastReboot")}
                        </div>
                        <p className="text-gray-700 font-semibold">{lastRebootTime}</p>
                    </motion.div>

                    <motion.div
                        className="bg-gradient-to-br from-teal-50 to-teal-100/70 p-3 rounded-xl shadow-sm border border-teal-200 relative overflow-hidden"
                        whileHover={{ scale: 1.03, y: -3, boxShadow: "0 8px 20px rgba(20, 184, 166, 0.15)" }}
                        initial={{ opacity: 0, x: 10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        <div className="text-teal-700 font-bold mb-1 flex items-center gap-1">
                            <RefreshCw className="w-3.5 h-3.5" />
                            {t("widgets.remoteReboot.stats.todayCount")}
                        </div>
                        <p className="text-gray-700 font-semibold">{new Intl.NumberFormat(i18n.language).format(todayRebootCount)} {t("widgets.remoteReboot.stats.times")}</p>
                    </motion.div>
                </div>

                {/* دکمه ری‌استارت */}
                <motion.button
                    onClick={handleReboot}
                    className={`w-full text-white py-3 rounded-xl text-sm font-semibold relative overflow-hidden shadow-lg ${
                        rebooted ? "bg-gradient-to-r from-green-500 to-emerald-600" : rebooting ? "bg-gradient-to-r from-amber-500 to-orange-500 cursor-not-allowed" : `bg-gradient-to-r ${color}`
                    }`}
                    whileHover={!rebooting && !rebooted ? { scale: 1.02, y: -2, boxShadow: "0px 8px 20px rgba(6, 182, 212, 0.3)" } : {}}
                    whileTap={!rebooting && !rebooted ? { scale: 0.98 } : {}}
                >
                    <motion.span
                        className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                        animate={rebooting ? { x: ["-100%", "100%"] } : { x: "-100%" }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                    />
                    <span className="relative z-10 flex items-center justify-center gap-2 font-semibold">
                        <motion.div animate={rebooting ? { rotate: [0, 360] } : { rotate: 0 }} transition={rebooting ? { duration: 1, repeat: Infinity, ease: "linear" } : {}}>
                            {rebooted ? <CheckCircle className="w-5 h-5" /> : <RotateCcw className="w-5 h-5" />}
                        </motion.div>
                        {rebooted ? t("widgets.remoteReboot.button.success") : rebooting ? t("widgets.remoteReboot.button.restarting") : t("widgets.remoteReboot.button.reboot")}
                    </span>
                </motion.button>

                {/* پیام راهنما */}
                <motion.div
                    className="mt-3 bg-gradient-to-r from-blue-50 to-sky-50 border border-blue-200 text-blue-700 text-xs p-2.5 rounded-lg flex items-center"
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                >
                    <motion.span
                        className="ml-2 bg-blue-100 w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                        animate={{ rotate: [0, -10, 10, 0] }}
                        transition={{ duration: 3, repeat: Infinity }}
                    >
                        <Lightbulb className="w-3.5 h-3.5" />
                    </motion.span>
                    <span>{t("widgets.remoteReboot.tip")}</span>
                </motion.div>
            </div>
        </motion.div>
    );
};

export default RemoteRebootWidget;